import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Save, Image, Loader2 } from "lucide-react";
import { toast } from "sonner";

const HeroEditor = ({ content, onSave }) => {
  const [formData, setFormData] = useState({
    badge_text: "",
    title_line1: "",
    title_line2: "",
    title_line3: "",
    subtitle: "",
    cta_primary: "",
    cta_secondary: "",
    background_image: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (content) {
      setFormData(content);
    }
  }, [content]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const success = await onSave(formData);
    setSaving(false);
    if (success) {
      toast.success("Hero actualizado correctamente");
    } else {
      toast.error("Error al guardar los cambios");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="hero-editor"
    >
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Editar Hero</h1>
          <p className="text-[#94a3b8]">Modifica la sección principal de tu página</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Textos Principales</h2>
          
          <div className="grid gap-4">
            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Badge (etiqueta superior)</label>
              <input
                type="text"
                name="badge_text"
                value={formData.badge_text}
                onChange={handleChange}
                className="contact-input w-full"
                placeholder="+20 Años de Experiencia"
              />
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Título Línea 1</label>
                <input
                  type="text"
                  name="title_line1"
                  value={formData.title_line1}
                  onChange={handleChange}
                  className="contact-input w-full"
                />
              </div>
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Título Línea 2 (destacado)</label>
                <input
                  type="text"
                  name="title_line2"
                  value={formData.title_line2}
                  onChange={handleChange}
                  className="contact-input w-full"
                />
              </div>
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Título Línea 3</label>
                <input
                  type="text"
                  name="title_line3"
                  value={formData.title_line3}
                  onChange={handleChange}
                  className="contact-input w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-[#94a3b8] text-sm mb-2">Subtítulo</label>
              <textarea
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                rows={3}
                className="contact-input w-full resize-none"
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Botón Principal</label>
                <input
                  type="text"
                  name="cta_primary"
                  value={formData.cta_primary}
                  onChange={handleChange}
                  className="contact-input w-full"
                />
              </div>
              <div>
                <label className="block text-[#94a3b8] text-sm mb-2">Botón Secundario</label>
                <input
                  type="text"
                  name="cta_secondary"
                  value={formData.cta_secondary}
                  onChange={handleChange}
                  className="contact-input w-full"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="glass-card p-6">
          <h2 className="text-xl font-semibold text-white mb-4">Imagen de Fondo</h2>
          
          <div>
            <label className="block text-[#94a3b8] text-sm mb-2">URL de la imagen</label>
            <div className="flex gap-4">
              <input
                type="url"
                name="background_image"
                value={formData.background_image}
                onChange={handleChange}
                className="contact-input flex-1"
                placeholder="https://..."
              />
              <div className="w-20 h-12 bg-[#0b1221] border border-white/10 overflow-hidden">
                {formData.background_image && (
                  <img src={formData.background_image} alt="Preview" className="w-full h-full object-cover" />
                )}
              </div>
            </div>
            <p className="text-[#94a3b8] text-xs mt-2">
              Puedes usar imágenes de Unsplash u otras URLs públicas
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <button
            type="submit"
            disabled={saving}
            className="btn-primary flex items-center gap-2"
          >
            {saving ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Save className="w-5 h-5" />
            )}
            <span>{saving ? "Guardando..." : "Guardar Cambios"}</span>
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default HeroEditor;
